import TextButton from '@shared/components/core/button/TextButton';
import CardStack from '@shared/components/core/card/CardStack';
import { CardStackLayout } from '@shared/components/core/card/CommonCardLayouts';
import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import { getRoute } from '@shared/hooks/getRoute';
import { publish } from '@shared/hooks/helpers/EventCallback';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

export default function ProfileAdvancedSettingsPageContent() {
    const { t } = useTranslation();
    const location = useLocation();
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const editProfileContainer = EditProfileContainer.useContainer();
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);

    const handleDelete = async () => {
        if (!editProfileContainer.id) return;
        publish('userProfileDeleted', { profileId: editProfileContainer.id });
        if (organizationId) {
            navigate(`/org/${organizationId}/profiles`, { replace: true });
        } else {
            navigate('/profiles', { replace: true });
        }
    };
    if (!editProfileContainer.commandKeyMappings) return null;
    return (
        <>
            <CardStackLayout>
                <CardStack name="touchpad" title={t('common:keys.title')} description={t('common:keys.description')}>
                    <TextButton variant="context" href={getRoute(`${location.pathname}/keybindings`)}>
                        {t('common:keys.edit')}
                    </TextButton>
                </CardStack>
                <CardStack
                    name="virtualButton"
                    title={t('common:hotkey.titlePlural')}
                    description={t('common:hotkey.description')}>
                    <TextButton variant="context" href={getRoute(`${location.pathname}/hotkeys`)}>
                        {t('common:hotkey.edit')}
                    </TextButton>
                </CardStack>
                <CardStack
                    name="dictationPlay"
                    title={t('common:dictation.title')}
                    description={t('common:dictation.description')}>
                    <TextButton variant="context" href={getRoute(`${location.pathname}/dictation`)}>
                        {t('common:dictation.edit')}
                    </TextButton>
                </CardStack>
                <CardStack
                    name="ai"
                    title={t('common:suggestedPrompts.title')}
                    description={t('common:suggestedPrompts.description')}>
                    <TextButton variant="context" href={getRoute(`${location.pathname}/suggested-prompts`)}>
                        {t('common:suggestedPrompts.edit')}
                    </TextButton>
                </CardStack>
                <CardStack
                    name="details"
                    title={t('common:profile.summary.title')}
                    description={t('common:profile.summary.description')}>
                    <TextButton
                        variant="context"
                        href={getRoute(`${location.pathname}/summary`)}
                        disabled={editProfileContainer.isPreview}>
                        {t('common:profile.summary.view')}
                    </TextButton>
                </CardStack>
                {editProfileContainer?.id && (
                    <CardStack
                        name="share"
                        title={t('common:share')}
                        description={t('common:profile.share.description')}>
                        <TextButton variant="context" href={getRoute(`${location.pathname}/share`)}>
                            {t('common:profile.share.title')}
                        </TextButton>
                    </CardStack>
                )}
                {editProfileContainer?.id && (
                    <CardStack
                        name="delete"
                        title={t('common:dangerZone')}
                        description={t('common:profile.delete.description')}>
                        <TextButton variant="context" onClick={() => setIsDeleteConfirmationOpen(true)}>
                            {t('common:profile.delete.title')}
                        </TextButton>
                    </CardStack>
                )}
            </CardStackLayout>
            {isDeleteConfirmationOpen && (
                <ContentDialog
                    open={isDeleteConfirmationOpen}
                    onCancel={() => setIsDeleteConfirmationOpen(false)}
                    title={t('common:profile.delete.confirm')}
                    description={t('common:profile.delete.confirmLong')}
                    onConfirm={handleDelete}
                    confirmButtonText={t('common:profile.delete.confirmButton')}
                    cancelButtonText={t('common:cancel')}
                    confirmIcon="delete"
                />
            )}
        </>
    );
}
